import React, { useState } from 'react';
import { Lock, Shield } from 'lucide-react';
import { use2FAStatus } from '@/hooks/useSettings';
import { useToast } from '@/hooks/useToast';
import ChangePasswordModal from './ChangePasswordModal';
import TwoFAModal from './TwoFAModal';
import ActiveSessions from './ActiveSessions';
import styles from './SecuritySection.module.css';

/**
 * SecuritySection Component
 * Security settings with password change, 2FA, and active sessions
 */
const SecuritySection = () => {
  const { showSuccess } = useToast();
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [twoFAModal, setTwoFAModal] = useState(false);
  
  // Use React Query hook for 2FA status
  const { data: twoFAData, isLoading } = use2FAStatus();
  const twoFaEnabled = twoFAData?.isEnabled || false;

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader}>
          <span className={styles.loaderText}>loading</span>
          <span className={styles.load}></span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.sectionTitle}>Security</h3>

      {/* Security Setup */}
      <div className={styles.securitySetup}>
        <h4 className={styles.setupTitle}>Security Setup</h4>
        <div className={styles.securityGrid}>
          {/* Change Password Card */}
          <div className={styles.securityCard}>
            <div className={styles.cardHeader}>
              <Lock className={styles.cardIcon} size={20} />
              <span className={styles.statusBadge}>Enabled</span>
            </div>
            <div className={styles.cardContent}>
              <h5 className={styles.cardTitle}>Change Password</h5>
              <p className={styles.cardDescription}>
                Change to a strong password to protect your account from unauthorized access.
              </p>
              <button
                className={styles.actionButton}
                onClick={() => setChangePasswordModal(true)}
              >
                Change Password
              </button>
            </div>
          </div>

          {/* 2FA Card */}
          <div className={styles.securityCard}>
            <div className={styles.cardHeader}>
              <Shield className={styles.cardIcon} size={20} />
              <span className={`${styles.statusBadge} ${twoFaEnabled ? styles.statusEnabled : styles.statusDisabled}`}>
                {twoFaEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className={styles.cardContent}>
              <h5 className={styles.cardTitle}>Two-factor authentication</h5>
              <p className={styles.cardDescription}>
                Enable Two-factor authentication to protect your account from unauthorized access.
              </p>
              <button
                className={styles.actionButton}
                onClick={() => {
                  setTwoFAModal(true);
                }}
              >
                {twoFaEnabled ? 'Manage 2FA' : 'Enable 2FA'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      <div className={styles.activeSessions}>
        <h4 className={styles.sessionsTitle}>Active Sessions</h4>
        <ActiveSessions />
      </div>

      {/* Modals */}
      <ChangePasswordModal
        isOpen={changePasswordModal}
        onClose={() => setChangePasswordModal(false)}
        onSuccess={() => {
          setChangePasswordModal(false);
          showSuccess('Password changed successfully!');
        }}
      />

      <TwoFAModal
        isOpen={twoFAModal}
        onClose={() => setTwoFAModal(false)}
        twoFaEnabled={twoFaEnabled}
      />
    </div>
  );
};

export default SecuritySection;
